import { FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import { format } from "date-fns";

import HubInputField from "src/components/inputType";
import StatsReports from "../manufacturer/statsReports";

import { TerritoriesPropsType, TerritoriesSearchProps } from "./types";


const timeData = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const filterCaseData = [
  { value: "all", label: "All Cases" },
  { value: "ecrall", label: "ECR Cases" },
  { value: "ncrall", label: "Non-ECR Cases" },
  { value: "parecrall", label: "PA Pending" },
  { value: "ncrparall", label: "Missing Docs" },
];




const TerritoriesFilter: FC<TerritoriesPropsType> = ({
  updateQueryParams,
  defaultQueryParams,
}): JSX.Element => {
  const { control, handleSubmit, getValues, reset } = useForm<TerritoriesSearchProps>({
    mode: "all",
    defaultValues: defaultQueryParams,
  });
  const [isClearing, setIsClearing] = useState(false);
  

  const clearFormData = () => {
    setIsClearing(true);
    const values = {
      start_date: format(
        new Date().setDate(new Date().getDate() - 30),
        "yyyy-MM-dd"
      ),
      end_date: format(
        new Date().setDate(new Date().getDate() + 1),
        "yyyy-MM-dd"
      ),
      period: "",
      filter: "",
      type: defaultQueryParams?.type,
      role: defaultQueryParams?.role,
      id: defaultQueryParams?.id,
      case_id: ""
    };

    reset(values);
    updateQueryParams(getValues(), true);
    setTimeout(() => setIsClearing(false), 0);

  };

  const searchForStats = (values: TerritoriesSearchProps) => {
    const updatedValues = {
      ...values,
      period: values.period
        ? typeof values.period === "string"
          ? values.period
          : (values.period as unknown as { value: string; label: string }).value
        : "",
      filter: values.filter
        ? typeof values.filter === "string"
          ? values.filter
          : (values.filter as unknown as { value: string; label: string }).value
        : "",
    };

    updateQueryParams(updatedValues, false);
  };

  return (
    <form onSubmit={handleSubmit(searchForStats)}>
      <div className="card mt-4">
        <div className="card-header">
          <h4 className="m-0">Statistics</h4>
        </div>

        <div className="card-body px-3 pt-3">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-10 col-lg-10">
              <div className="row g-2">
                <div className="col-12 col-sm-12 col-md-2">
                  <Controller
                    name="start_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value ? new Date(field.value) : new Date()}
                        onChange={(date) => date ? field.onChange(date) : ""}
                        customInput={<ReactInputMask mask="99/99/9999">{(inputProps: {}) => <input {...inputProps} />}</ReactInputMask>}
                        className="form-control rounded-1" wrapperClassName="w-100"
                        placeholderText="mm/dd/yyyy"
                      />
                    )}
                  />
                </div>

                <div className="col-12 col-sm-12 col-md-2">
                  <Controller
                    name="end_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value ? new Date(field.value) : new Date()}
                        onChange={(date) => date ? field.onChange(date) : ""}
                        customInput={<ReactInputMask mask="99/99/9999">{(inputProps: {}) => <input {...inputProps} />}</ReactInputMask>}
                        className="form-control rounded-1" wrapperClassName="w-100"
                        placeholderText="mm/dd/yyyy"
                      />
                    )}
                  />
                </div>

                <div className="col-12 col-sm-12 col-md-2 mb-3">
                  <Controller
                    name="period"
                    control={control}
                    defaultValue={timeData[0].value}
                    render={({ field }) => (
                      <Select
                        isClearable={true}
                        options={
                          (timeData && timeData.length > 0
                            ? timeData.map((item) => ({
                              value: item.value,
                              label: item.label,
                            }))
                            : []
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                        id="time"
                        {...field}
                      />
                    )}
                  />
                </div>

                <div className="col-12 col-sm-12 col-md-2 mb-3">
                  <Controller
                    name="filter"
                    control={control}
                    render={({ field }) => (
                      <Select
                        isClearable={true}
                        options={
                          (filterCaseData && filterCaseData.length > 0
                            ? filterCaseData.map((item) => ({
                                value: item.value,
                                label: item.label,
                            }))
                            : []
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                        id="filter"
                        {...field}
                        placeholder={"Filter ..."}
                      />
                    )}
                  />
                </div>

                <div className="col-12 col-sm-12 col-md-2  mb-3">
                  <Controller
                    name="case_id"
                    control={control}
                    render={({ field }) => (
                      <HubInputField
                        inputId="case_id"
                        field={field}
                        inputPlaceholder="Case ID"
                        isRequired={false}
                        isRowBased={false}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4 col-lg-2 mb-3 d-flex gap-2">
              <button type="submit" className="btn btn-primary w-100">
                <i className="bi bi-arrow-repeat text-white"></i> Refresh
              </button>
              <button type="button" className="btn btn-light border-secondary w-100" disabled={isClearing} onClick={() => clearFormData()}>
                <i className="bi bi-eraser-fill"></i> Clear
              </button>
              
            </div>
          </div>

          <StatsReports getValues={getValues} />
        </div>
      </div>
    </form>
  );
};

export default TerritoriesFilter;
